/**
 * Shared utility functions for NowAssist Skill Kit tool processing.
 * Contains helpers used across multiple tool processing modules.
 */

import {
    TABLE_NAMES,
    TOOL_TYPES,
    TOOL_RESOURCE_CAPABILITIES,
    RESOURCE_MAPPING_APPLICABILITY_SCRIPT,
    RESOURCE_ATTRIBUTE_VALUE_SCRIPT,
    INVALID_TOOL_INTERNAL_TYPES,
    VALID_TOOL_DATA_TYPES,
} from './defaults'
import {
    type Record as BuildRecord,
    Shape,
    PropertyAccessShape,
    ElementAccessExpressionShape,
    StringLiteralShape,
    TemplateExpressionShape,
    type Source,
} from '@servicenow/sdk-build-core'
import type { ToolDefinition } from './tool-types'

// Destructure table names for easier usage
export const {
    SYS_ONE_EXTEND_RESOURCE_MAPPING,
    SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING,
    SYS_ONE_EXTEND_RESOURCE_EDGE,
    SYS_ONE_EXTEND_CAPABILITY,
    SYS_ONE_EXTEND_CAPABILITY_DEFINITION,
    SYS_ONE_EXTEND_DEFINITION_CONFIG,
    SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE,
    SYS_GEN_AI_FEATURE_MAPPING,
    SYS_GEN_AI_STRATEGY_MAPPING,
} = TABLE_NAMES

/**
 * A thin Shape wrapper that emits a raw code string via getCode().
 * Used only when existing shape classes cannot produce the exact code needed:
 * - Bracket notation access: t.input['doc id'], Now.ID['skill-name']
 * - TypeScript 'as const' assertions on arrays
 * - Decision branch item objects (complex condition templates)
 * All other patterns (arrow functions, return objects, defaults) use proper shapes.
 */
export class RawCodeShape extends Shape<string> {
    private readonly code: string

    constructor({ source, code }: { source: Source; code: string }) {
        super({ source })
        this.code = code
    }

    override getCode(): string {
        return this.code
    }
}

/**
 * Normalize a skill input field name for use in template expressions.
 *
 * All names are lowercased. Names with spaces also get underscores:
 *   "Incident Number" → "incident_number"
 *   "User Query"      → "user_query"
 *   "Sales Rep ID"    → "sales_rep_id"
 *   "userQuery"        → "userquery"
 *   "incidentNumber"   → "incidentnumber"
 */
export function normalizeInputFieldName(name: string): string {
    return name.toLowerCase().replace(/ +/g, '_')
}

/**
 * Convert a property chain from fluent code (after t.input.) to mustache template format.
 * Only the first segment (input name) is normalized; the rest is preserved as-is.
 * Array bracket notation [N] is converted to .[N] format.
 *
 * Examples:
 *   "stringtestname"             → "stringtestname"
 *   "ACE.assigned_to.active"     → "ace.assigned_to.active"
 *   "jsonArray[0].name"          → "jsonarray.[0].name"
 *   "ACE"                        → "ace"
 */
export function inputChainToTemplate(chain: string): string {
    const firstSep = chain.search(/[.[]/)
    if (firstSep === -1) {
        return normalizeInputFieldName(chain)
    }
    const inputName = chain.substring(0, firstSep)
    let rest = chain.substring(firstSep)
    // Ensure bracket access is preceded by a dot: [0] → .[0], .foo[0] → .foo.[0]
    rest = rest.replace(/(?<!\.)\[/g, '.[')
    return normalizeInputFieldName(inputName) + rest
}

/**
 * Resolve a shape value to a template string for attribute_value_template.
 *
 * Handles these patterns:
 * - String literal: returns the literal value as-is (e.g., 'perplexity' → 'perplexity')
 * - t.input.fieldName (PropertyAccessShape): converts to {{fieldname}}
 * - t.input.ACE.assigned_to.active (deep dot-walk): converts to {{ace.assigned_to.active}}
 * - t.input['Field Name'] (ElementAccessExpressionShape): converts to {{field_name}}
 * - t.input.ACE['assigned_to'] (nested bracket): converts to {{ace.assigned_to}}
 * - toolVar.outputField (PropertyAccessShape): looks up tool name → {{ToolName.outputField}}
 * - Template literals: `text ${t.input.field}` → 'text {{field}}'
 */
export function resolveTemplateValue(shape: Shape | undefined, toolNameToVarName: Map<string, string>): string {
    if (!shape) {
        return ''
    }

    // TemplateExpressionShape: handles template literals with interpolation
    // Must check BEFORE isString() since TemplateExpressionShape extends StringShape
    // Example: `text ${t.input.field}` → 'text {{field}}'
    if (shape instanceof TemplateExpressionShape) {
        let result = shape.getLiteralText()

        for (const span of shape.getSpans()) {
            const expression = span.getExpression()
            const replacement = resolveExpression(expression, toolNameToVarName)
            result += replacement + span.getLiteralText()
        }

        return result
    }

    // String literals: return as-is (checked after TemplateExpressionShape since it also extends StringShape)
    if (shape.isString()) {
        return shape.asString().getValue()
    }

    // PropertyAccessShape / ElementAccessExpressionShape / other
    return resolveExpression(shape, toolNameToVarName)
}

/**
 * Resolve a single expression shape to a template string.
 * Shared by resolveTemplateValue for both standalone and template-span expressions.
 */
function resolveExpression(expression: Shape, toolNameToVarName: Map<string, string>): string {
    if (expression instanceof PropertyAccessShape) {
        const elements = expression.getElements()
        const firstElement = elements[0]?.getName()

        // Pattern: t.input.fieldName[.deepWalk...] → {{fieldname[.deepwalk...]}}
        if (elements.length >= 3 && elements[1]?.getName() === 'input') {
            const fieldName = elements[2]?.getName()
            if (fieldName) {
                const normalized = normalizeInputFieldName(fieldName)
                if (elements.length === 3) {
                    return `{{${normalized}}}`
                }
                const rest = elements
                    .slice(3)
                    .map((e) => e.getName())
                    .join('.')
                return `{{${normalized}.${rest}}}`
            }
        }

        // Pattern: toolVar.outputField → {{ToolName.outputField}}
        if (elements.length === 2 && firstElement) {
            const toolName = toolNameToVarName.get(firstElement)
            if (toolName) {
                const outputField = elements[1]?.getName()
                if (outputField) {
                    return `{{${toolName}.${outputField}}}`
                }
            }
        }
    }

    if (expression instanceof ElementAccessExpressionShape) {
        const callee = expression.getCallee()
        const arg = expression.getArg()

        // Pattern: t.input['Field Name'] → {{field_name}}
        if (callee.endsWith('.input') && arg.isString()) {
            return `{{${normalizeInputFieldName(arg.asString().getValue())}}}`
        }

        // Pattern: t.input.ACE['assigned_to'] → {{ace.assigned_to}}
        // Pattern: t.input.jsonArray[0] → {{jsonarray.[0]}}
        if (callee.includes('.input.')) {
            const match = callee.match(/\.input\.(.+)$/)
            if (match && match[1]) {
                if (arg.isString()) {
                    return `{{${inputChainToTemplate(match[1])}.${arg.asString().getValue()}}}`
                }
                return `{{${inputChainToTemplate(match[1])}.[${arg.getCode()}]}}`
            }
        }

        // Pattern: toolVar['outputField'] → {{ToolName.outputField}}
        if (arg.isString()) {
            const toolName = toolNameToVarName.get(callee)
            if (toolName) {
                return `{{${toolName}.${arg.asString().getValue()}}}`
            }
        }
    }

    // Fallback: detect input references from code (e.g., t.input.jsonArray[0].name)
    const code = expression.getCode()
    if (code) {
        const inputMatch = code.match(/^[a-zA-Z_$]+\.input\.(.+)$/)
        if (inputMatch && inputMatch[1]) {
            return `{{${inputChainToTemplate(inputMatch[1])}}}`
        }
    }

    return code || ''
}

/**
 * Get the resource capability sys_id for a tool type.
 * For tool types with fixed platform capability IDs (inline-script, web-search, decision, skill),
 * returns the known sys_id.
 * For dynamic tool types (script, subflow, flow-action), the caller must provide the
 * tool's external ID (scriptId, subflowId, actionId) as the resource_capability since
 * these tools create per-skill capability definitions at runtime.
 */
export function getResourceCapability(toolType: ToolDefinition['type'], externalId?: string): string {
    switch (toolType) {
        case TOOL_TYPES.INLINE_SCRIPT:
            return TOOL_RESOURCE_CAPABILITIES.INLINE_SCRIPT
        case TOOL_TYPES.WEB_SEARCH:
            return TOOL_RESOURCE_CAPABILITIES.WEB_SEARCH
        case TOOL_TYPES.DECISION:
            return TOOL_RESOURCE_CAPABILITIES.DECISION
        case TOOL_TYPES.SKILL:
            // Skill-as-Tool uses the TARGET skill's sys_one_extend_capability sys_id
            // as resource_capability. The skillId provided by the user IS the target
            // skill's capability sys_id, not a fixed platform ID.
            // Falls back to the fixed platform ID if no skillId was provided.
            return externalId || TOOL_RESOURCE_CAPABILITIES.SKILL
        case TOOL_TYPES.SCRIPT:
        case TOOL_TYPES.SUBFLOW:
        case TOOL_TYPES.FLOW_ACTION:
            // Dynamic tools use their external ID as the capability reference.
            // The tool type is stored in metadata for reverse detection.
            return externalId || ''
        default:
            throw new Error(`Unknown tool type: ${toolType}`)
    }
}

/**
 * Wrap a user-provided condition script body in the standard IIFE template.
 * If the script already starts with '(function', it is returned as-is.
 * Otherwise the body is injected into the standard applicability function wrapper.
 */
export function wrapConditionScript(scriptBody: string): string {
    const trimmed = scriptBody.trim()
    if (trimmed.startsWith('(function')) {
        return trimmed
    }
    return `(function(currentInputs, context) { /* @param {Object} currentInputs - Inputs provided for the current step.*/ /* @param {Object} context - Execution context, which may include:*/ /* - Previous tool outputs/inputs (e.g., context['ToolName.attributeName'] or context.getValue('ToolName.attributeName')).*/ /* - Additional context information (e.g., context.getAllValues()).*/ /* @returns {Boolean} - Return 'true' if the condition is met; otherwise 'false'.*/ ${trimmed} })(currentInputs, context);`
}

/**
 * Generate the attribute_value_script for a tool input attribute mapping.
 *
 * When the template value references a skill input (e.g., `{{incidentnumber}}`),
 * the script returns `currentInputs.<inputName>` so the platform can resolve
 * the input at runtime even when falling back from template to script evaluation.
 *
 * When the value references another tool's output (e.g., `{{ToolName.field}}`),
 * the script uses `context.getValue('ToolName.field')` to retrieve it.
 *
 * For static values or unrecognized patterns, the default script template is used.
 *
 * @param inputName - The original input field name (e.g., 'incidentNumber')
 * @param templateValue - The resolved template value (e.g., '{{incidentnumber}}')
 * @returns The attribute_value_script content
 */
export function generateInputAttributeValueScript(inputName: string, templateValue: string): string {
    // Match skill input reference: {{fieldname}}
    const skillInputMatch = templateValue.match(/^\{\{([^.}]+)\}\}$/)
    if (skillInputMatch) {
        // Use the original inputName for currentInputs access (preserves camelCase)
        return `(function execute(currentInputs, context) {\n    return currentInputs.${inputName} || '';\n})(currentInputs, context);`
    }

    // Match tool output reference: {{ToolName.field}}
    const toolOutputMatch = templateValue.match(/^\{\{([^.}]+)\.([^}]+)\}\}$/)
    if (toolOutputMatch) {
        const toolRef = `${toolOutputMatch[1]}.${toolOutputMatch[2]}`
        return `(function execute(currentInputs, context) {\n    return context.getValue('${toolRef}') || '';\n})(currentInputs, context);`
    }

    // Static value or unrecognized pattern: use default script
    return RESOURCE_ATTRIBUTE_VALUE_SCRIPT
}

/**
 * Get applicability properties for resource mapping based on tool condition
 * Returns properties to spread into the resource mapping record
 * - If no condition or type='none': only sets applicability_type='none'
 * - If condition provided: sets both applicability_type and applicability_script
 *   with the user's script body wrapped in the standard IIFE template
 */
export function getApplicabilityProperties(
    condition: ToolDefinition['condition'],
    $: { val: (v: unknown) => { toCdata: () => unknown } & unknown }
): Record<string, unknown> {
    const applicabilityType = condition?.type || ''
    const applicabilityScript = condition?.script
        ? wrapConditionScript(condition.script)
        : RESOURCE_MAPPING_APPLICABILITY_SCRIPT

    return {
        applicability_type: $.val(applicabilityType),
        applicability_script: $.val(applicabilityScript).toCdata(),
    }
}

/**
 * Extract dependencies for a tool from edge records.
 * Dependencies are tools whose IDs appear as from_resource in edges where to_resource = this tool's ID.
 * Excludes the parent skill mapping (self-mapping edges) and Decision routing edges
 * (edges from Decision tools to their targets — these are routing, not dependency relationships).
 * Returns array of all dependency names found.
 */
export function extractDependsFromEdges(
    mappingId: string,
    edges: BuildRecord[],
    mappingIdToName: Map<string, string>,
    decisionMappingIds?: Set<string>
): string[] | undefined {
    const deps: string[] = []
    for (const edge of edges) {
        const toId = edge.get('to_resource')?.getValue() as string
        const fromId = edge.get('from_resource')?.getValue() as string
        if (toId === mappingId && fromId) {
            // Skip edges from Decision tools — these are routing edges, not dependency edges.
            // Decision routing is captured by targets/branches/default, not by depends.
            if (decisionMappingIds?.has(fromId)) {
                continue
            }
            const depName = mappingIdToName.get(fromId)
            if (depName && !deps.includes(depName)) {
                deps.push(depName)
            }
        }
    }
    return deps.length > 0 ? deps : undefined
}

/**
 * Extract condition (applicability) from a resource mapping record.
 */
export function extractConditionFromMapping(mapping: BuildRecord): { type?: string; script?: string } | undefined {
    const applicabilityType = mapping.get('applicability_type')?.getValue() as string
    if (!applicabilityType || applicabilityType === 'none' || applicabilityType === '') {
        return undefined
    }
    const applicabilityScript = mapping.get('applicability_script')?.getValue() as string
    // Skip default scripts (they're just boilerplate)
    const isDefaultScript =
        applicabilityScript?.includes('return true;') && applicabilityScript?.includes('currentInputs, context')
    if (applicabilityType === 'script' && applicabilityScript && !isDefaultScript) {
        return { type: 'script', script: applicabilityScript }
    }
    return undefined
}

/**
 * Get attribute mappings for a specific resource mapping, split into inputs and outputs.
 */
export function getAttributeMappingsForTool(
    mappingId: string,
    attributeMappings: BuildRecord[]
): { inputs: BuildRecord[]; outputs: BuildRecord[] } {
    const inputs: BuildRecord[] = []
    const outputs: BuildRecord[] = []
    for (const attr of attributeMappings) {
        const rmId = attr.get('resource_mapping')?.getValue() as string
        if (rmId === mappingId) {
            const type = attr.get('type')?.getValue() as string
            if (type === 'input') {
                inputs.push(attr)
            } else if (type === 'output') {
                outputs.push(attr)
            }
        }
    }
    return { inputs, outputs }
}

/**
 * Check if a string looks like a sys_id (32-character hexadecimal).
 */
export function isSysId(value: string): boolean {
    return /^[0-9a-f]{32}$/i.test(value)
}

/**
 * Validate that a tool input/output type is valid for FlowAction, Subflow, or PI tools.
 * Returns an error message if invalid, or null if valid.
 *
 * @param type - The type value to validate
 * @param fieldName - Name of the input/output field (for error message)
 * @param toolName - Name of the tool (for error message)
 * @param isInput - Whether this is an input (true) or output (false)
 */
export function validateToolDataType(
    type: string | undefined,
    fieldName: string,
    toolName: string,
    isInput: boolean
): string | null {
    // If type is not provided, it's valid (will use default)
    if (!type) {
        return null
    }

    const fieldType = isInput ? 'input' : 'output'

    // Check if type contains spaces - suggest proper format
    if (/\s/.test(type)) {
        const suggestedType = type.toLowerCase().replace(/\s+/g, '_')
        return (
            `Tool '${toolName}' has ${fieldType} '${fieldName}' with invalid type '${type}'. ` +
            `Type values should not contain spaces. ` +
            `Did you mean '${suggestedType}'?`
        )
    }

    // Normalize type to lowercase for case-insensitive comparison
    const normalizedType = type.toLowerCase()

    // Check against invalid internal types (from AI Studio)
    if (INVALID_TOOL_INTERNAL_TYPES.includes(normalizedType as (typeof INVALID_TOOL_INTERNAL_TYPES)[number])) {
        return (
            `Tool '${toolName}' has ${fieldType} '${fieldName}' with invalid type '${type}'. ` +
            `The following types are not supported for FlowAction/Subflow tools: ${INVALID_TOOL_INTERNAL_TYPES.join(', ')}. ` +
            `Please use a valid type: ${VALID_TOOL_DATA_TYPES.join(', ')}.`
        )
    }

    return null
}

/**
 * Resolve resource_attribute_name from sys_id to human-readable name.
 * The XML stores sys_ids in resource_attribute_name (referencing sys_one_extend_definition_attribute),
 * but the parser discards the display_value. This function uses the definition attribute lookup map
 * to restore the original name.
 *
 * Returns undefined if the raw value is a sys_id that cannot be resolved — this indicates
 * a stale/phantom attribute mapping from a previous build (e.g., leftover from Record API mode
 * after deleting a cross-scope tool). Such phantom records should be skipped by the caller.
 */
export function resolveAttributeName(rawValue: string, defAttrNameMap?: Map<string, string>): string | undefined {
    if (!defAttrNameMap || defAttrNameMap.size === 0) {
        return rawValue
    }
    const resolved = defAttrNameMap.get(rawValue)
    if (resolved) {
        return resolved
    }
    // If the raw value is a sys_id that couldn't be resolved, it's a phantom/stale reference
    // from a previous build — skip it rather than using the sys_id as the attribute name
    if (isSysId(rawValue)) {
        return undefined
    }
    // Non-sys_id values (e.g., already human-readable names) pass through as-is
    return rawValue
}

/**
 * Topological sort of tools to ensure depended-upon tools are declared before their dependents.
 * Falls back to original order for tools without dependency relationships.
 */
export function topologicalSortTools(tools: ToolDefinition[]): ToolDefinition[] {
    const toolMap = new Map<string, ToolDefinition>()
    for (const tool of tools) {
        toolMap.set(tool.name, tool)
    }

    const sorted: ToolDefinition[] = []
    const visited = new Set<string>()
    const visiting = new Set<string>() // cycle detection

    function visit(tool: ToolDefinition): void {
        if (visited.has(tool.name)) {
            return
        }
        if (visiting.has(tool.name)) {
            return
        } // cycle -- skip
        visiting.add(tool.name)

        // Visit dependencies first (they must be declared before this tool)
        if (tool.depends && tool.depends.length > 0) {
            for (const depName of tool.depends) {
                const dep = toolMap.get(depName)
                if (dep) {
                    visit(dep)
                }
            }
        }

        visiting.delete(tool.name)
        visited.add(tool.name)
        sorted.push(tool)
    }

    for (const tool of tools) {
        visit(tool)
    }

    return sorted
}

/**
 * Sanitize a tool name for use as a JavaScript variable name.
 * Replaces non-alphanumeric characters with underscores and ensures it starts with a letter.
 */
export function sanitizeVarName(name: string): string {
    let varName = name.replace(/[^a-zA-Z0-9_$]/g, '_')
    if (/^[0-9]/.test(varName)) {
        varName = '_' + varName
    }
    return varName
}

/**
 * Convert a template value string (e.g., '{{query}}', '{{ToolName.output}}', 'literal')
 * back into the appropriate Shape for use in generated Fluent code.
 *
 * Simple patterns are converted to fluent expressions for intellisense:
 * - '{{fieldName}}'           → PropertyAccessShape: t.input.fieldName
 * - '{{ToolName.outputField}}' → PropertyAccessShape: varName.outputField
 *
 * Complex patterns (dot-walk, array access, mixed templates) are kept as string literals
 * so users see the {{...}} format and can convert to fluent syntax for intellisense:
 * - '{{ace.assigned_to.active}}'  → StringLiteralShape: '{{ace.assigned_to.active}}'
 * - '{{jsonarray.[0].name}}'      → StringLiteralShape: '{{jsonarray.[0].name}}'
 * - 'text {{field}} more'         → StringLiteralShape: 'text {{field}} more'
 * - 'literal'                     → StringLiteralShape: 'literal'
 */
export function templateValueToShape(
    source: Source,
    value: string,
    paramName: string,
    toolNameToVarName: Map<string, string>,
    inputNameMap?: Map<string, string>
): Shape {
    // Match pure template expressions like {{...}}
    const match = value.match(/^\{\{(.+)\}\}$/)
    if (match && match[1]) {
        const inner: string = match[1]
        const dotIdx = inner.indexOf('.')
        if (dotIdx > 0) {
            const firstSegment = inner.substring(0, dotIdx)
            const outputField = inner.substring(dotIdx + 1)
            // Check tool output reference: {{ToolName.outputField}}
            const varName = toolNameToVarName.get(firstSegment)
            if (varName) {
                return new PropertyAccessShape({
                    source,
                    elements: [varName, outputField],
                })
            }
            // Could be input.fieldName pattern (shouldn't happen with {{}} but handle anyway)
            if (firstSegment === 'input') {
                const originalOutputField = inputNameMap?.get(outputField) ?? outputField
                if (/[^a-zA-Z0-9_$]/.test(originalOutputField)) {
                    return new RawCodeShape({
                        source,
                        code: `${paramName}.input['${originalOutputField}']`,
                    })
                }
                return new PropertyAccessShape({
                    source,
                    elements: [paramName, 'input', originalOutputField],
                })
            }
            // Complex dotted input reference (dot-walk, array access, etc.)
            // Keep as string literal — user can convert to fluent syntax for intellisense
            return new StringLiteralShape({ source, literalText: value })
        }
        // Simple field name — treat as input reference: t.input.fieldName
        // Use inputNameMap to restore original casing (XML stores lowercased names)
        const originalName = inputNameMap?.get(inner) ?? inner
        // Use bracket notation for names with spaces/special chars: t.input['doc id']
        if (/[^a-zA-Z0-9_$]/.test(originalName)) {
            return new RawCodeShape({
                source,
                code: `${paramName}.input['${originalName}']`,
            })
        }
        return new PropertyAccessShape({
            source,
            elements: [paramName, 'input', originalName],
        })
    }
    // Not a template or mixed template — literal string
    return new StringLiteralShape({ source, literalText: value })
}
